/**
 * Minimal hash router — no routing library (spec: "no component framework", keep the bundle
 * lean). Routes below are a frontend decision the spec's endpoint table doesn't dictate: the
 * artifact.json contract has no session id, so the run/artifact routes carry it in the path to
 * support the "Continue in chat" button (see the apps/web report's deviations section).
 */
import { useSyncExternalStore } from 'react';

/** The admin pages (docs/specs/accounts.md, section 8). The pages themselves are package F. */
export const ADMIN_PAGES = ['users', 'runs', 'health'] as const;
export type AdminPage = (typeof ADMIN_PAGES)[number];

export type Route =
  | { view: 'home' }
  | { view: 'session'; sessionId: string }
  | { view: 'run'; sessionId: string; runId: string }
  | { view: 'artifact'; sessionId: string; runId: string }
  /** `returnTo` is the hash the person wanted before a 401 sent them here, already validated. */
  | { view: 'login'; returnTo: string | null }
  | { view: 'setup'; token: string }
  | { view: 'account' }
  | { view: 'admin'; page: AdminPage };

/** Routes that render without the app shell and need no login. */
export function isPublicRoute(route: Route): boolean {
  return route.view === 'login' || route.view === 'setup';
}

const RETURN_PARAM = 'return';

export function parseHash(hash: string): Route {
  // Strip the `?query` half (artifact-view filter state, v1.1) before splitting into path
  // segments — a filter query string must never change which view a hash resolves to.
  const [path = '', query = ''] = splitHash(hash);
  const parts = path.split('/').filter(Boolean);
  if (parts[0] === 'login' && parts.length === 1) {
    return { view: 'login', returnTo: safeReturnTo(new URLSearchParams(query).get(RETURN_PARAM)) };
  }
  if (parts[0] === 'setup' && parts[1] && parts.length === 2) {
    return { view: 'setup', token: safeDecode(parts[1]) };
  }
  if (parts[0] === 'account' && parts.length === 1) return { view: 'account' };
  if (parts[0] === 'admin' && parts.length === 2) {
    const page = ADMIN_PAGES.find((p) => p === parts[1]);
    if (page) return { view: 'admin', page };
  }
  // sessions/:id
  if (parts[0] === 'sessions' && parts[1]) {
    if (parts[2] === 'runs' && parts[3]) {
      if (parts[4] === 'artifact') return { view: 'artifact', sessionId: parts[1], runId: parts[3] };
      return { view: 'run', sessionId: parts[1], runId: parts[3] };
    }
    return { view: 'session', sessionId: parts[1] };
  }
  return { view: 'home' };
}

/** A malformed escape in a pasted link must not throw out of the router. */
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** A hash, split at its first `?` into path and query. */
function splitHash(hash: string): [string, string] {
  const body = hash.replace(/^#/, '');
  const qIndex = body.indexOf('?');
  return qIndex === -1 ? [body, ''] : [body.slice(0, qIndex), body.slice(qIndex + 1)];
}

/**
 * The hash to go back to after a login, or null. Only a hash inside this app qualifies: it must
 * start with `#/`, and it must not be a public route, because returning to the login page after a
 * login is a loop. Anything else is dropped, so a crafted link cannot send a person elsewhere.
 */
export function safeReturnTo(candidate: string | null | undefined): string | null {
  if (!candidate || !candidate.startsWith('#/')) return null;
  const route = parseHash(candidate);
  if (isPublicRoute(route) || route.view === 'home') return null;
  return candidate;
}

/**
 * Where a 401 sends the person: the login route, carrying the hash they were on. The return hash
 * rides in the URL rather than in memory so that it survives a reload of the login page.
 */
export function loginHashFor(currentHash: string): string {
  const returnTo = safeReturnTo(currentHash);
  return returnTo ? `#/login?${RETURN_PARAM}=${encodeURIComponent(returnTo)}` : '#/login';
}

function subscribe(callback: () => void): () => void {
  window.addEventListener('hashchange', callback);
  return () => window.removeEventListener('hashchange', callback);
}

// useSyncExternalStore requires a referentially-stable snapshot when nothing changed — a fresh
// object per call (even with identical fields) trips its "getSnapshot should be cached" loop
// guard. Cache by the raw hash string and only reparse when it actually changes.
const HOME_ROUTE: Route = { view: 'home' };
let cachedHash: string | null = null;
let cachedRoute: Route = HOME_ROUTE;

function getSnapshot(): Route {
  const hash = window.location.hash;
  if (hash !== cachedHash) {
    cachedHash = hash;
    cachedRoute = parseHash(hash);
  }
  return cachedRoute;
}

function getServerSnapshot(): Route {
  return HOME_ROUTE;
}

export function useRoute(): Route {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function navigate(route: Route): void {
  window.location.hash = routePath(route);
}

/**
 * Go to `hash` and leave no history entry for the hash being left. For a route whose hash holds a
 * secret — a setup token — so that the token is not one Back press away once it has been used.
 * `replaceState` fires no `hashchange`, so the router's subscribers are told by hand.
 */
export function replaceHash(hash: string): void {
  window.history.replaceState(null, '', hash);
  window.dispatchEvent(new Event('hashchange'));
}

export function routePath(route: Route): string {
  switch (route.view) {
    case 'home':
      return '#/';
    case 'session':
      return `#/sessions/${route.sessionId}`;
    case 'run':
      return `#/sessions/${route.sessionId}/runs/${route.runId}`;
    case 'artifact':
      return `#/sessions/${route.sessionId}/runs/${route.runId}/artifact`;
    case 'login':
      return route.returnTo ? loginHashFor(route.returnTo) : '#/login';
    case 'setup':
      return `#/setup/${encodeURIComponent(route.token)}`;
    case 'account':
      return '#/account';
    case 'admin':
      return `#/admin/${route.page}`;
  }
}

// ---- Artifact-view filter state (v1.1) — the `?query` half of the hash ----
//
// Kept here rather than in lib/filters.ts because it touches `window.location` directly (the
// hash is one string shared with route parsing above); lib/filters.ts stays pure/URL-agnostic
// so its (de)serializers are trivially unit-testable without a DOM.

/** The current hash's query half, parsed. Empty when there is none. */
export function currentHashQuery(): URLSearchParams {
  const hash = window.location.hash.replace(/^#/, '');
  const qIndex = hash.indexOf('?');
  return new URLSearchParams(qIndex === -1 ? '' : hash.slice(qIndex + 1));
}

/**
 * Rewrites the query half of the hash in place, preserving the path. Uses `replaceState` (not
 * `location.hash = `) deliberately: assigning `location.hash` fires `hashchange`, which would
 * loop straight back into whatever listener called this on every filter keystroke, and would
 * spam browser history with one entry per keystroke besides. `replaceState` does neither.
 */
export function setHashQuery(params: URLSearchParams): void {
  const hash = window.location.hash.replace(/^#/, '');
  const qIndex = hash.indexOf('?');
  const path = qIndex === -1 ? hash : hash.slice(0, qIndex);
  const qs = params.toString();
  const next = qs ? `${path}?${qs}` : path;
  if (window.location.hash !== `#${next}`) {
    window.history.replaceState(null, '', `#${next}`);
  }
}
